// Session de l'usager du portail citoyen, distincte de la session des agents : le jeton vient de
// POST /public/connexion (comptes de type usager seulement) et la session n'a ni appartenance ni
// contexte de collectivité. Le jeton d'accès et le rafraîchissement sont ceux du client HTTP
// commun (apps/web/src/api/client.ts) : un seul compte est connecté à la fois dans le navigateur.
// La session est « usager » quand GET /usager/profil répond ; un compte agent connecté est signalé
// comme tel (le portail exige un compte usager).
import type { SessionOuverte } from '@cc/contrats';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { definirJeton, ecouterSession, jetonPresent, rafraichir, requete, requetePublique } from '../../../api/client';
import { ErreurApi } from '../../../api/erreurs';
import type { ProfilUsager } from '../types';

export type EtatSessionUsager = 'initialisation' | 'deconnecte' | 'connecte' | 'compte_agent';

interface ValeurSessionUsager {
  etat: EtatSessionUsager;
  profil: ProfilUsager | null;
  chargementProfil: boolean;
  /** Vrai quand la session a expiré pendant l'utilisation. */
  expiree: boolean;
  connecter: (identifiant: string, motDePasse: string) => Promise<void>;
  /** Ouvre la session à partir d'une réponse de connexion déjà obtenue (fin d'inscription). */
  ouvrir: (s: SessionOuverte) => void;
  recharger: () => Promise<unknown>;
  deconnecter: () => Promise<void>;
}

const ContexteSessionUsager = createContext<ValeurSessionUsager | null>(null);

export const CLE_PROFIL_USAGER = ['citoyen', 'profil'] as const;

export function FournisseurSessionUsager({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [etat, setEtat] = useState<EtatSessionUsager>(() => (jetonPresent() ? 'connecte' : 'initialisation'));
  const [expiree, setExpiree] = useState(false);

  // Reprise de la session existante par le cookie de rafraîchissement (même mécanisme que les agents).
  useEffect(() => {
    if (etat !== 'initialisation') return;
    let actif = true;
    void rafraichir().then((ok) => {
      if (actif) setEtat(ok ? 'connecte' : 'deconnecte');
    });
    return () => {
      actif = false;
    };
  }, [etat]);

  useEffect(
    () =>
      ecouterSession((evenement) => {
        if (evenement === 'session_expiree') {
          setExpiree(true);
          setEtat('deconnecte');
          client.removeQueries({ queryKey: ['citoyen'] });
        }
      }),
    [client],
  );

  const requeteProfil = useQuery({
    queryKey: CLE_PROFIL_USAGER,
    queryFn: ({ signal }) => requete<ProfilUsager>('/usager/profil', { signal }),
    enabled: etat === 'connecte',
    staleTime: 60_000,
    retry: false,
  });

  // Un jeton valide qui n'est pas celui d'un usager (compte agent) : le portail le dit, sans fermer sa session.
  useEffect(() => {
    if (etat === 'connecte' && requeteProfil.error instanceof ErreurApi && requeteProfil.error.code === 'compte_usager_requis') setEtat('compte_agent');
  }, [etat, requeteProfil.error]);

  const ouvrir = useCallback(
    (s: SessionOuverte) => {
      client.removeQueries({ queryKey: ['citoyen'] });
      definirJeton(s.jeton_acces, s.expire_dans);
      setExpiree(false);
      setEtat('connecte');
    },
    [client],
  );

  const connecter = useCallback(
    async (identifiant: string, motDePasse: string) => {
      const r = await requetePublique<SessionOuverte>('/public/connexion', { identifiant, mot_de_passe: motDePasse });
      ouvrir(r);
    },
    [ouvrir],
  );

  const deconnecter = useCallback(async () => {
    try {
      await requete('/auth/deconnexion', { methode: 'POST' });
    } catch {
      // Session déjà close côté serveur : la déconnexion locale suffit.
    }
    definirJeton(null);
    client.removeQueries({ queryKey: ['citoyen'] });
    setExpiree(false);
    setEtat('deconnecte');
  }, [client]);

  const valeur = useMemo<ValeurSessionUsager>(
    () => ({
      etat,
      profil: etat === 'connecte' ? (requeteProfil.data ?? null) : null,
      chargementProfil: etat === 'connecte' && requeteProfil.isPending,
      expiree,
      connecter,
      ouvrir,
      recharger: () => requeteProfil.refetch(),
      deconnecter,
    }),
    [etat, requeteProfil, expiree, connecter, ouvrir, deconnecter],
  );

  return <ContexteSessionUsager.Provider value={valeur}>{children}</ContexteSessionUsager.Provider>;
}

export function useSessionUsager(): ValeurSessionUsager {
  const v = useContext(ContexteSessionUsager);
  if (!v) throw new Error('useSessionUsager hors de FournisseurSessionUsager');
  return v;
}

/** Prénom d'usage pour le salut de l'accueil (« Bonjour Albertine »). */
export function prenomUsager(p: ProfilUsager | null): string {
  if (!p) return '';
  return (p.prenoms ?? '').split(/\s+/).filter(Boolean)[0] ?? p.nom;
}
